import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PRODUCT_CATEGORIES_QUERY_KEY,
  PRODUCT_SUBCATEGORIES_QUERY_KEY,
} from '@/api/queries/product-categories/key';
import {
  createProductCategory,
  deleteProductCategory,
  deleteProductSubcategory,
  updateProductCategory,
  updateProductSubcategory,
} from './request';
import type { ProductCategory } from '@/types';
import toast from 'react-hot-toast';

function invalidateCategoryQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  categoryId?: number
) {
  queryClient.invalidateQueries({
    queryKey: [PRODUCT_CATEGORIES_QUERY_KEY],
  });
  if (categoryId) {
    queryClient.invalidateQueries({
      queryKey: [PRODUCT_SUBCATEGORIES_QUERY_KEY, categoryId],
    });
  }
}

export const useCreateProductCategory = (
  onSuccess?: (category: ProductCategory) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProductCategory,
    onSuccess: ({ response, data }) => {
      toast.success(response || 'Category saved successfully');
      invalidateCategoryQueries(queryClient, data?.id);
      if (data) onSuccess?.(data);
    },
  });
};

export const useUpdateProductCategory = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProductCategory,
    onSuccess: ({ response }, variables) => {
      toast.success(response || 'Category updated successfully');
      invalidateCategoryQueries(queryClient, variables.id);
      onSuccess?.();
    },
  });
};

export const useDeleteProductCategory = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProductCategory,
    onSuccess: ({ response }) => {
      toast.success(response || 'Category deleted successfully');
      invalidateCategoryQueries(queryClient);
      onSuccess?.();
    },
  });
};

export const useUpdateProductSubcategory = (
  categoryId: number,
  onSuccess?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProductSubcategory,
    onSuccess: ({ response }) => {
      toast.success(response || 'Subcategory updated successfully');
      invalidateCategoryQueries(queryClient, categoryId);
      onSuccess?.();
    },
  });
};

export const useDeleteProductSubcategory = (
  categoryId: number,
  onSuccess?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProductSubcategory,
    onSuccess: ({ response }) => {
      toast.success(response || 'Subcategory deleted successfully');
      invalidateCategoryQueries(queryClient, categoryId);
      onSuccess?.();
    },
  });
};
